import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import {
  ArrowUpRight,
  Plus,
  CheckCircle,
  WarningOctagon,
  Clock,
  Wallet,
  Stack,
  ArrowSquareOut,
  Camera,
  HouseLine,
  CaretRight,
  CaretDown,
  ChartBar,
} from "@phosphor-icons/react";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const STATUS_META = {
  open: { label: "Open", color: "bg-[#E8F0FE] text-[#1967D2] border-[#AECBFA]" },
  accepted: { label: "Accepted", color: "bg-[#E6F4EA] text-[#137333] border-[#A4D0B3]" },
  partially_fulfilled: { label: "Partial", color: "bg-[#FEF7E0] text-[#B06000] border-[#FCE39E]" },
  rejected: { label: "Rejected", color: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
};

export default function Dashboard() {
  const { activeProject, activeProjectId } = useProject();
  const [stats, setStats] = useState(null);
  const [outwardStats, setOutwardStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeProjectId) return;
    (async () => {
      setLoading(true);
      try {
        const [s, r, o, c] = await Promise.all([
          api.get("/pos/stats"),
          api.get("/pos", { params: { page: 1, page_size: 8 } }),
          api.get("/outward/stats"),
          api.get("/boq/coverage", { params: { project_id: activeProjectId } }),
        ]);
        setStats(s.data);
        const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
        setRecent(items.slice(0, 8));
        setOutwardStats(o.data);
        setCoverage(c.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeProjectId]);

  const counts = stats?.by_status || {};
  const openCount = counts.open?.count || 0;
  const acceptedCount = counts.accepted?.count || 0;
  const partialCount = counts.partially_fulfilled?.count || 0;
  const rejectedCount = counts.rejected?.count || 0;

  return (
    <div className="p-8 sm:p-10 max-w-[1600px] mx-auto" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2 flex items-center gap-2">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: activeProject?.color || "#0047FF" }}
            />
            Project Dashboard
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight" data-testid="dashboard-project-name">
            {activeProject?.name || "Procurement Dashboard"}
          </h1>
          <p className="text-sm text-neutral-500 mt-2">
            Real-time overview of purchase orders, deliveries, and invoice reconciliation for this project.
          </p>
        </div>
        <Link to="/pos/new">
          <Button className="rounded-sm h-11 px-5 bg-primary hover:bg-[#0039CC] text-white font-medium gap-2" data-testid="raise-po-btn">
            <Plus size={16} weight="bold" />
            Raise New PO
          </Button>
        </Link>
      </div>

      {/* Stat cards – bento */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          testId="stat-total"
          icon={<Stack size={18} weight="bold" />}
          label="Total POs"
          value={stats?.total_count ?? "—"}
          sub="All time"
          accent="text-neutral-900"
        />
        <StatCard
          testId="stat-value"
          icon={<Wallet size={18} weight="bold" />}
          label="Total Value"
          value={fmtINR(stats?.total_value)}
          sub="Committed spend"
          accent="text-primary"
        />
        <StatCard
          testId="stat-open"
          icon={<Clock size={18} weight="bold" />}
          label="Open"
          value={openCount}
          sub="Awaiting delivery"
          accent="text-[#1967D2]"
        />
        <StatCard
          testId="stat-rejected"
          icon={<WarningOctagon size={18} weight="bold" />}
          label="Rejected"
          value={rejectedCount}
          sub={`${partialCount} partial · ${acceptedCount} accepted`}
          accent="text-[#C5221F]"
        />
      </div>

      {/* BOQ Coverage */}
      <BOQCoverageCard coverage={coverage} loading={loading} />

      {/* Bento: categories + recent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Categories */}
        <div className="bg-white border border-neutral-200 rounded-sm lg:col-span-1">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">By Category</div>
              <div className="font-display text-xl font-bold mt-0.5">Material Split</div>
            </div>
          </div>
          <div className="p-4">
            {stats?.by_category?.length ? (
              <div className="space-y-2">
                {stats.by_category
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 8)
                  .map((cat) => {
                    const pct =
                      stats.total_count > 0 ? Math.round((cat.count / stats.total_count) * 100) : 0;
                    return (
                      <div key={cat.category} data-testid={`cat-row-${cat.category}`}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-neutral-800">{cat.category}</span>
                          <span className="text-neutral-500 tabular-nums">{cat.count}</span>
                        </div>
                        <div className="h-1.5 bg-neutral-100 overflow-hidden">
                          <div
                            className="h-full bg-primary"
                            style={{ width: `${pct}%`, transition: "width 400ms ease" }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-sm text-neutral-400 py-8 text-center">No categories yet</div>
            )}
          </div>
        </div>

        {/* Recent POs */}
        <div className="bg-white border border-neutral-200 rounded-sm lg:col-span-2">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Recent Activity</div>
              <div className="font-display text-xl font-bold mt-0.5">Latest Purchase Orders</div>
            </div>
            <Link to="/pos" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="view-all-pos-link">
              View all <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600">
                <tr>
                  <th className="text-left px-6 py-3 font-medium text-xs uppercase tracking-wider">PO Number</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Vendor</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Description</th>
                  <th className="text-right px-4 py-3 font-medium text-xs uppercase tracking-wider">Amount</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-10 text-neutral-400">Loading…</td>
                  </tr>
                ) : recent.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-16">
                      <img
                        src="https://static.prod-images.emergentagent.com/jobs/8edb9b1d-d169-4c51-9caf-4228cc8b337f/images/de968366efd48c55ff7284c3d62573ca222caf2a683936c63f3b28f840f65706.png"
                        alt="No POs"
                        className="h-24 mx-auto mb-4 opacity-90"
                      />
                      <div className="text-sm text-neutral-500 mb-3">No purchase orders yet</div>
                      <Link to="/pos/new">
                        <Button size="sm" className="rounded-sm bg-primary hover:bg-[#0039CC]">
                          <Plus size={14} className="mr-1" /> Raise First PO
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ) : (
                  recent.map((po) => {
                    const meta = STATUS_META[po.status] || STATUS_META.open;
                    // Combine subcategory + first line-item descriptions for a quick "what's in this PO?" glance
                    const lineDescs = (po.line_items || [])
                      .map((li) => li?.description)
                      .filter(Boolean);
                    const description =
                      po.subcategory ||
                      lineDescs.slice(0, 2).join(", ") +
                        (lineDescs.length > 2 ? ` +${lineDescs.length - 2}` : "");
                    return (
                      <tr key={po.id} className="border-t border-neutral-100 hover:bg-neutral-50/70 transition-colors" data-testid={`recent-po-row-${po.po_number}`}>
                        <td className="px-6 py-3">
                          <Link to={`/pos/${po.id}`} className="font-mono-num text-primary hover:underline">
                            {po.po_number}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-neutral-800">{po.vendor_name}</td>
                        <td className="px-4 py-3 text-neutral-600">{po.category}</td>
                        <td className="px-4 py-3 text-neutral-600 max-w-xs" title={description || ""}>
                          <div className="truncate text-xs">{description || <span className="text-neutral-300">—</span>}</div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{fmtINR(po.total_amount)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2 py-0.5 border ${meta.color}`}>
                            {po.status === "accepted" && <CheckCircle size={12} weight="fill" />}
                            {meta.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Outward stats bento */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <StatCard
          testId="outward-total"
          icon={<ArrowSquareOut size={18} weight="bold" />}
          label="Outward Entries"
          value={outwardStats?.total_count ?? "—"}
          sub={`${outwardStats?.by_type?.bulk || 0} bulk · ${outwardStats?.by_type?.flat || 0} flat-wise`}
          accent="text-[#6B21A8]"
        />

        {/* Top flats */}
        <div className="bg-white border border-neutral-200 rounded-sm">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Flat-wise Consumption</div>
              <div className="font-display text-lg font-bold mt-0.5">Top Flats</div>
            </div>
            <HouseLine size={18} className="text-neutral-400" />
          </div>
          <div className="p-4">
            {outwardStats?.top_flats?.length ? (
              <div className="space-y-2">
                {outwardStats.top_flats.slice(0, 5).map((f) => {
                  const max = outwardStats.top_flats[0]?.count || 1;
                  const pct = Math.round((f.count / max) * 100);
                  return (
                    <div key={f.flat_number} data-testid={`top-flat-${f.flat_number}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-mono-num text-neutral-800">{f.flat_number}</span>
                        <span className="text-neutral-500 tabular-nums text-xs">{f.count} issues</span>
                      </div>
                      <div className="h-1.5 bg-neutral-100 overflow-hidden">
                        <div
                          className="h-full bg-[#6B21A8]"
                          style={{ width: `${pct}%`, transition: "width 400ms ease" }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-neutral-400 py-6 text-center">No flat-wise entries yet</div>
            )}
          </div>
        </div>

        {/* Outward by category */}
        <div className="bg-white border border-neutral-200 rounded-sm">
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Material Issued</div>
              <div className="font-display text-lg font-bold mt-0.5">Outward by Category</div>
            </div>
            <Camera size={18} className="text-neutral-400" />
          </div>
          <div className="p-4">
            {outwardStats?.by_category?.length ? (
              <div className="space-y-2">
                {[...outwardStats.by_category]
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 6)
                  .map((c) => {
                    const max = Math.max(...outwardStats.by_category.map((x) => x.count)) || 1;
                    const pct = Math.round((c.count / max) * 100);
                    return (
                      <div key={c.category} data-testid={`outward-cat-${c.category}`}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-neutral-800">{c.category}</span>
                          <span className="text-neutral-500 tabular-nums text-xs">{c.count}</span>
                        </div>
                        <div className="h-1.5 bg-neutral-100 overflow-hidden">
                          <div
                            className="h-full bg-neutral-700"
                            style={{ width: `${pct}%`, transition: "width 400ms ease" }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-sm text-neutral-400 py-6 text-center">No outward entries yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sub, accent, testId }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-sm p-5 hover:shadow-sm hover:-translate-y-[1px] transition-all duration-150" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">{label}</div>
        <div className="h-7 w-7 bg-neutral-100 flex items-center justify-center text-neutral-700">{icon}</div>
      </div>
      <div className={`font-display text-3xl font-bold tabular-nums ${accent}`}>{value}</div>
      <div className="text-xs text-neutral-500 mt-1">{sub}</div>
    </div>
  );
}


/* ============================================================
 * BOQ Coverage Card + drill-down
 * ============================================================ */
const COVERAGE_STATUS_META = {
  not_started: { label: "Not Started", color: "text-neutral-600", bg: "bg-neutral-100", bar: "bg-neutral-400" },
  in_progress: { label: "In Progress", color: "text-[#1967D2]", bg: "bg-[#E8F0FE]", bar: "bg-[#1967D2]" },
  complete:    { label: "Complete",    color: "text-[#137333]", bg: "bg-[#E6F4EA]", bar: "bg-[#137333]" },
  over_budget: { label: "Over Budget", color: "text-[#C5221F]", bg: "bg-[#FCE8E6]", bar: "bg-[#C5221F]" },
};

function BOQCoverageCard({ coverage, loading }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const [filterStatus, setFilterStatus] = useState("all");

  const totals = coverage?.totals || { total_items: 0, not_started: 0, in_progress: 0, complete: 0, over_budget: 0, original_budget: 0, ordered_amount: 0, coverage_percent: 0 };
  const categories = coverage?.categories || [];
  const filteredCats = filterStatus === "all"
    ? categories
    : categories.filter((c) => c.status === filterStatus || c.items?.some((it) => it.status === filterStatus));

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="bg-white border border-neutral-200 rounded-sm mb-8" data-testid="boq-coverage-card">
      <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 bg-primary/10 text-primary flex items-center justify-center rounded-sm">
            <ChartBar size={16} weight="bold" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-neutral-500">Procurement Coverage</div>
            <div className="font-display text-xl font-bold mt-0.5">BOQ vs. Purchase Orders</div>
          </div>
        </div>
        <Link to="/boq" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="boq-coverage-manage-link">
          Manage BOQ <ArrowUpRight size={14} />
        </Link>
      </div>

      {loading || !coverage ? (
        <div className="p-10 text-center text-neutral-400 text-sm">Loading coverage…</div>
      ) : totals.total_items === 0 ? (
        <div className="p-10 text-center">
          <div className="text-sm text-neutral-500 mb-3">No BOQ items in this project yet.</div>
          <Link to="/boq"><Button className="rounded-sm bg-primary hover:bg-[#0039CC]" data-testid="boq-coverage-empty-cta">Start Building BOQ</Button></Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 border-b border-neutral-200">
            <SegmentTile status="not_started" label="Not Started" count={totals.not_started} total={totals.total_items} active={filterStatus === "not_started"} onClick={() => setFilterStatus(filterStatus === "not_started" ? "all" : "not_started")} testId="coverage-seg-not-started" />
            <SegmentTile status="in_progress" label="In Progress" count={totals.in_progress} total={totals.total_items} active={filterStatus === "in_progress"} onClick={() => setFilterStatus(filterStatus === "in_progress" ? "all" : "in_progress")} testId="coverage-seg-in-progress" />
            <SegmentTile status="complete" label="Complete" count={totals.complete} total={totals.total_items} active={filterStatus === "complete"} onClick={() => setFilterStatus(filterStatus === "complete" ? "all" : "complete")} testId="coverage-seg-complete" />
            <SegmentTile status="over_budget" label="Over Budget" count={totals.over_budget} total={totals.total_items} active={filterStatus === "over_budget"} onClick={() => setFilterStatus(filterStatus === "over_budget" ? "all" : "over_budget")} testId="coverage-seg-over-budget" />
          </div>

          <div className="px-6 py-4 border-b border-neutral-200 bg-neutral-50/50">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <div className="text-neutral-500">
                Overall Coverage · <span className="tabular-nums text-neutral-900 font-medium">{fmtINR(totals.ordered_amount)}</span> of <span className="tabular-nums text-neutral-900">{fmtINR(totals.original_budget)}</span>
              </div>
              <div className="tabular-nums font-medium text-neutral-900" data-testid="coverage-overall-pct">{totals.coverage_percent}%</div>
            </div>
            <div className="h-2 bg-neutral-100 overflow-hidden rounded-sm">
              <div className="h-full bg-primary transition-all" style={{ width: Math.min(totals.coverage_percent, 100) + "%" }} />
            </div>
          </div>

          {filterStatus !== "all" && (
            <div className="px-6 py-2 text-[11px] text-neutral-500 bg-[#FEF7E0] border-b border-neutral-200">
              Filtered by <span className="font-medium">{COVERAGE_STATUS_META[filterStatus]?.label}</span> ·{" "}
              <button onClick={() => setFilterStatus("all")} className="underline text-primary" data-testid="coverage-clear-filter">Clear filter</button>
            </div>
          )}
          <div className="divide-y divide-neutral-100 max-h-[540px] overflow-y-auto" data-testid="coverage-category-list">
            {filteredCats.length === 0 ? (
              <div className="p-8 text-center text-sm text-neutral-400">No categories match this filter.</div>
            ) : filteredCats.map((c) => {
              const isOpen = expanded.has(c.category);
              const meta = COVERAGE_STATUS_META[c.status] || COVERAGE_STATUS_META.in_progress;
              const filteredItems = filterStatus === "all" ? c.items : c.items.filter((it) => it.status === filterStatus);
              return (
                <div key={c.category} data-testid={`coverage-cat-${c.category}`}>
                  <button
                    onClick={() => toggle(c.category)}
                    className="w-full px-6 py-3 flex items-center gap-3 hover:bg-neutral-50/70 text-left"
                    data-testid={`coverage-toggle-${c.category}`}
                  >
                    <span className="w-5 flex-shrink-0 text-neutral-400">
                      {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-sm text-neutral-900 truncate">{c.category}</span>
                        <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${meta.bg} ${meta.color}`}>{meta.label}</span>
                        <span className="text-[11px] text-neutral-400 tabular-nums ml-auto">{c.item_count} item{c.item_count !== 1 ? "s" : ""}</span>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-neutral-500">
                        <div className="flex-1 h-1.5 bg-neutral-100 overflow-hidden rounded-sm max-w-[400px]">
                          <div className={`h-full transition-all ${meta.bar}`} style={{ width: Math.min(c.percent_ordered, 100) + "%" }} />
                        </div>
                        <span className="tabular-nums w-12 text-right">{c.percent_ordered.toFixed(0)}%</span>
                        <span className="tabular-nums text-neutral-700 whitespace-nowrap">{fmtINR(c.ordered_amount)} / {fmtINR(c.boq_amount)}</span>
                      </div>
                    </div>
                  </button>
                  {isOpen && filteredItems.length > 0 && (
                    <div className="pl-14 pr-6 pb-3 space-y-1 bg-neutral-50/40" data-testid={`coverage-items-${c.category}`}>
                      {filteredItems.map((it) => {
                        const iMeta = COVERAGE_STATUS_META[it.status] || COVERAGE_STATUS_META.in_progress;
                        return (
                          <Link
                            to={`/boq/item/${it.id}`}
                            key={it.id}
                            className="flex items-center gap-3 text-xs py-1.5 hover:text-primary"
                            data-testid={`coverage-item-${it.id}`}
                          >
                            <span className="flex-1 truncate text-neutral-800">{it.name}</span>
                            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${iMeta.bg} ${iMeta.color}`}>{iMeta.label}</span>
                            <span className="tabular-nums text-neutral-500 whitespace-nowrap">
                              {it.ordered_qty.toFixed(2)} / {it.total_qty.toFixed(2)} {it.unit || ""}
                            </span>
                            <span className="tabular-nums text-neutral-500 w-12 text-right">{it.percent_ordered.toFixed(0)}%</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function SegmentTile({ status, label, count, total, active, onClick, testId }) {
  const meta = COVERAGE_STATUS_META[status] || COVERAGE_STATUS_META.in_progress;
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={
        "px-5 py-4 border-r last:border-r-0 border-neutral-200 text-left hover:bg-neutral-50/70 transition-colors " +
        (active ? "bg-primary/5 ring-2 ring-inset ring-primary" : "")
      }
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`text-[10px] uppercase tracking-wider ${meta.color}`}>{label}</span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded-sm ${meta.bg} ${meta.color} tabular-nums`}>{pct}%</span>
      </div>
      <div className="font-display text-2xl font-bold tabular-nums text-neutral-900">{count}</div>
      <div className="text-[10px] text-neutral-500 mt-0.5">of {total} items</div>
    </button>
  );
}
